/**
 * UI-side theme model. Decoupled from `data-access-tenant`'s `THEME_CONFIG`
 * (which uses snake_case server keys) so this layer never depends on the
 * data-access lib.
 *
 * - `primaryColor` / `secondaryColor` mirror `domain-data.THEME_CONFIG` /
 *   `fb-shipper-frontend` naming (navy vs accent). They are **not** swapped here —
 *   `applyTheme()` maps them to CSS the same way as `freightify-web`
 *   `LayoutComponent.setThemeColors`: `--p-primary-*` / `--primary` use the
 *   **secondary** API colour (accent fill), while `--secondary` / `--app-brand-*`
 *   use the **primary** API colour (outlined strokes / navy).
 * - `darkMode` toggles a CSS class on `<html>` to flip PrimeNG's dark variant.
 */
export interface Theme {
  readonly primaryColor: string;
  readonly secondaryColor: string;
  readonly darkMode: boolean;
}

/**
 * 50–950 PrimeNG-compatible color shade map. Keys mirror Tailwind's default
 * shade ladder, which PrimeNG's Aura preset also consumes via
 * `--p-primary-{50…950}` CSS custom properties.
 */
export interface ShadeScale {
  readonly 50: string;
  readonly 100: string;
  readonly 200: string;
  readonly 300: string;
  readonly 400: string;
  readonly 500: string;
  readonly 600: string;
  readonly 700: string;
  readonly 800: string;
  readonly 900: string;
  readonly 950: string;
}

/** Default Freightify branding when domain-data is unavailable. */
export const DEFAULT_THEME: Theme = {
  primaryColor: '#2563eb', // tailwind blue-600 — matches our existing brand swatch
  secondaryColor: '#64748b', // tailwind slate-500
  darkMode: false,
};
